import {Injectable} from "@angular/core";
import {BehaviorSubject, finalize, map, Observable, Subject, takeUntil, tap} from "rxjs";
import {SFTPAPIService, UserSettings} from "@app/services/sftp-api/sftp-api.service";
import {ChildNodesPayload, DeleteNodePayload} from "@app/services/sftp-api/models";
import {NodeTypes, TreeNode} from "@app/modules/models";

export enum BrowseContentMode {
  BrowseTable = 1,
  UserSettings,
}

@Injectable()
export class BrowseService {
  private _files$: BehaviorSubject<TreeNode[]> = null;
  private _browseContentMode$: BehaviorSubject<BrowseContentMode> = null;
  private _fileDownloading: boolean;
  private _filesBatchDownloading: boolean;
  private _fileDeleting: boolean;
  private _childNodesLoading: boolean;

  private _destroy$: Subject<void>;

  constructor(private sftpAPIService: SFTPAPIService) {}

  initialize(): void {
    this._destroy$ = new Subject<void>();
    this._files$ = new BehaviorSubject<TreeNode[]>(null);
    this._browseContentMode$ = new BehaviorSubject<BrowseContentMode>(BrowseContentMode.BrowseTable);

    this._fileDownloading = false;
    this._filesBatchDownloading = false;
    this._fileDeleting = false;
    this._childNodesLoading = false;
  }

  destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._destroy$ = null;

    this._files$.complete();
    this._files$ = null;

    this._fileDownloading = false;
    this._filesBatchDownloading = false;
    this._fileDeleting = false;
    this._childNodesLoading = false;
  }

  get fileDownloading(): boolean {
    return this._fileDownloading;
  }

  get filesBatchDownloading(): boolean {
    return this._filesBatchDownloading;
  }

  get fileDeleting(): boolean {
    return this._fileDeleting;
  }

  get files$(): Observable<TreeNode[]> {
    return this._files$.asObservable();
  }

  get childNodesLoading(): boolean {
    return this._childNodesLoading;
  }

  get browseContentMode$(): Observable<BrowseContentMode> {
    return this._browseContentMode$.asObservable();
  }

  updateBrowseContentMode(mode: BrowseContentMode): void {
    this._browseContentMode$.next(mode);
  }

  onFolderSelect(node: TreeNode): Observable<TreeNode[]> {
    const input = {
      node_name: node.data,
      node_type: NodeTypes.File,
    };

    return this.getChildNodes(input).pipe(
      takeUntil(this._destroy$),
      tap((data: TreeNode[]) => {
        this._files$.next(data);
      })
    );
  }

  downloadFile(filePath: string): Observable<BlobPart> {
    this._fileDownloading = true;

    return this.sftpAPIService.downloadFile({path: filePath}).pipe(
      finalize(() => {
        this._fileDownloading = false;
      })
    );
  }

  downloadFilesBatch(filePaths: string[]): Observable<BlobPart> {
    this._filesBatchDownloading = true;

    return this.sftpAPIService.downloadFilesBatch({paths: filePaths}).pipe(
      takeUntil(this._destroy$),
      finalize(() => {
        this._filesBatchDownloading = false;
      })
    );
  }

  getChildNodes(input: ChildNodesPayload): Observable<TreeNode[]> {
    this._childNodesLoading = true;

    return this.sftpAPIService.getChildNodes(input).pipe(
      takeUntil(this._destroy$),
      map((response) => response.data),
      finalize(() => (this._childNodesLoading = false))
    );
  }

  deleteNode(input: DeleteNodePayload): Observable<void> {
    this._fileDeleting = true;

    return this.sftpAPIService.deleteNode(input).pipe(
      takeUntil(this._destroy$),
      finalize(() => {
        this._fileDeleting = false;
      })
    );
  }

  getUserSettings(): Observable<UserSettings> {
    return this.sftpAPIService.getUserSettings().pipe(takeUntil(this._destroy$));
  }
}
